// src/components/TagTriggers.tsx
import React, { useEffect, useReducer, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotesStore, CueNote } from "../state/notes-store";
import { notesReducer } from "../reducers/notesReducer";
import { createPacket, setCurrentPacket, listPackets, getCurrentPacketId } from "../persistence/packets";
import { STARTER_NOTES } from "../notes.starter";
import { Package, MoreVertical } from "lucide-react";
import Page from "./Page";
import PageTitle from "./PageTitle";
import Hint, { useResetHintsFromQuery } from "./Hint";
import CueBoardCard from "./CueBoardCard";
import EmptyStateBounce from "./EmptyStateBounce";
import "./components.css";

const TAG_HINT_KEYS = ["header", "notes", "triggers", "savebar"] as const;
const SHOW_KEYPHRASES_KEY = "cueboard.ui.showKeyPhrases";

export default function TagTriggers() {
  const navigate = useNavigate();
  const { notes: initialNotes, setNotes: saveNotesToStore, currentPacketId, setCurrentPacketId } = useNotesStore();
  const [notes, dispatch] = useReducer(notesReducer, initialNotes as CueNote[]);

  const saveMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const saveKebabBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    dispatch({ type: "SET_NOTES", payload: initialNotes as CueNote[] });
  }, [initialNotes]);

  const [packetName, setPacketName] = useState<string>("");
  const [activePid, setActivePid] = useState<string | null>(null);

  const [hintRefreshToken, setHintRefreshToken] = useState(0);
  const resetRefreshToken = useResetHintsFromQuery("cueboard.hint.tag.");
  const combinedRefreshToken = resetRefreshToken + hintRefreshToken;

  const [hintsHidden, setHintsHidden] = useState<boolean>(() => {
    try {
      return TAG_HINT_KEYS.every((k) => localStorage.getItem(`cueboard.hint.tag.${k}`) === "dismissed");
    } catch { return false; }
  });

  const [showKeyPhrases, setShowKeyPhrases] = useState<boolean>(() => {
    try { return localStorage.getItem(SHOW_KEYPHRASES_KEY) === "1"; }
    catch { return false; }
  });

  const [starterAdded, setStarterAdded] = useState<boolean>(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);

  const STARTER_TITLE_SET = useMemo(() => {
    const s = new Set<string>();
    (STARTER_NOTES as CueNote[]).forEach((n) => s.add((n.title || "").trim().toLowerCase()));
    return s;
  }, []);

  useEffect(() => {
    const hasStarterId = notes.some((note) => typeof note.id === "string" && note.id.startsWith("starter-"));
    let hasStarter = hasStarterId;
    if (!hasStarter && notes.length > 0) {
      const titlesInPacket = new Set(notes.map((note) => (note.title || "").trim().toLowerCase()));
      let match = 0;
      STARTER_TITLE_SET.forEach((t) => { if (titlesInPacket.has(t)) match++; });
      const threshold = Math.ceil((STARTER_TITLE_SET.size || 1) * 0.6);
      if (match >= threshold) hasStarter = true;
    }
    setStarterAdded(hasStarter);
  }, [notes, STARTER_TITLE_SET]);

  useEffect(() => {
    const fetchPacketInfo = async () => {
      try {
        const id = currentPacketId || await getCurrentPacketId();
        setActivePid(id);
        if (id) {
          const packets = await listPackets();
          const found = packets.find(p => p.id === id);
          setPacketName(found?.name || "Untitled Prep Kit");
        } else {
          setPacketName("Untitled Prep Kit");
        }
      } catch {
        setActivePid(null);
        setPacketName("Untitled Prep Kit");
      }
    };
    fetchPacketInfo();
  }, [currentPacketId]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!saveMenuOpen || !saveMenuWrapRef.current) return;
      if (!saveMenuWrapRef.current.contains(e.target as Node)) {
        setSaveMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [saveMenuOpen]);

  const buildWorkingSnapshot = (): CueNote[] => {
    return notes.map(note => {
      const seen = new Set<string>();
      const uniq = (note.triggers ?? []).map(t => t.trim()).filter(Boolean).filter(t => {
        const k = t.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      return { ...note, id: note.id, title: (note.title || "").trim(), body_markdown: note.body_markdown, triggers: uniq };
    });
  };

  const onSaveOnly = async () => { await saveNotesToStore(buildWorkingSnapshot()); alert("Saved CueBoards"); };
  const onSaveAs = async () => {
    const base = (packetName && packetName.trim()) ? packetName.trim() : "Packet";
    const defaultName = `${base} — Copy ${new Date().toLocaleDateString()}`;
    const name = prompt("Save As — new packet name:", defaultName);
    if (!name) return;
    const p = await createPacket(name, buildWorkingSnapshot());
    await setCurrentPacket(p.id);
    await setCurrentPacketId(p.id);
    alert(`Saved as "${p.name}" and switched to it.`);
  };
  const onSaveAndPreview = async () => { await saveNotesToStore(buildWorkingSnapshot()); navigate("/setup/preview"); };

  // Add GENERAL pack only
  const addStarterPack = async () => {
    const starter: CueNote[] = (STARTER_NOTES as CueNote[]).map((n) => ({ ...n, id: `starter-${crypto.randomUUID()}` }));
    const merged = [...buildWorkingSnapshot(), ...starter];
    await saveNotesToStore(merged);
    alert(`Added ${starter.length} CueBoards from the General Pack`);
  };

  const toggleShowKeyPhrases = () => {
    setShowKeyPhrases(cur => {
      const next = !cur;
      try { localStorage.setItem(SHOW_KEYPHRASES_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  const markAllHints = (state: "dismissed" | null) => {
    TAG_HINT_KEYS.forEach((k) => {
      const key = `cueboard.hint.tag.${k}`;
      try {
        if (state === "dismissed") localStorage.setItem(key, "dismissed");
        else localStorage.removeItem(key);
      } catch {}
    });
    // bump token so Hint components re-read localStorage
    setHintRefreshToken(t => t + 1);
  };

  const toggleHints = () => {
    if (hintsHidden) { markAllHints(null); setHintsHidden(false); }
    else { markAllHints("dismissed"); setHintsHidden(true); }
  };

  // --- NEW: Floating jump arrows (contextual) ---
  const [showJumpUp, setShowJumpUp] = useState(false);
  const [showJumpDown, setShowJumpDown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const max = (document.documentElement.scrollHeight - window.innerHeight) || 0;

      // Only show if the page is meaningfully longer than the viewport
      const tallEnough = (document.documentElement.scrollHeight - window.innerHeight) > 160;

      setShowJumpUp(tallEnough && y > 80);
      setShowJumpDown(tallEnough && (max - y) > 120);
    };
    onScroll(); // seed on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function jump(dir: "up" | "down") {
    const delta = Math.round(window.innerHeight * 0.85);
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const target = dir === "up" ? Math.max(0, y - delta) : y + delta;
    window.scrollTo({ top: target, behavior: "smooth" });
  }

  return (
    <Page>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <PageTitle>Tag Your CueBoards</PageTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label className="small muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <input type="checkbox" checked={showKeyPhrases} onChange={toggleShowKeyPhrases} aria-label="Show key phrases" />
            Show key trigger phrases
          </label>
          <button className="button button-ghost" style={{ fontSize: 12, padding: "2px 6px" }} onClick={toggleHints}>
            {hintsHidden ? "Unhide Tag Hints" : "Hide Tag Hints"}
          </button>
        </div>
      </div>

      <div className="muted" style={{ fontSize: 15, lineHeight: "20px", marginTop: -6, marginBottom: 8 }}>
        Prep Kit: <strong>{packetName}</strong>
      </div>

      {/* TOP ACTIONS (single source of truth for Add controls) */}
      <div className="header-actions">
        <div className="button-group" style={{ marginTop: 0, marginBottom: 0 }}>
          <button className="button button-secondary" onClick={() => dispatch({ type: "ADD_NOTE" })}>+ Add CueBoard</button>
          <button className="button button-primary" onClick={addStarterPack} disabled={starterAdded}
            title={starterAdded ? "General Pack has already been added" : "Add the General (universal) Pack to this kit"}>
            <Package size={16} className="mr-2" />
            {starterAdded ? "General Starter Pack Added" : "Add General Starter Pack"}
          </button>
        </div>
      </div>

      {/* Body */}
      {notes.length === 0 ? (
        <EmptyStateBounce />
      ) : (
        <div className="notes-grid">
          {notes.map((note, idx) => (
            <CueBoardCard
              key={note.id}
              note={note}
              index={idx}
              dispatch={dispatch}
              activePacketId={activePid}
              showHints={idx === 0}
              showKeyPhrases={showKeyPhrases}
              combinedRefreshToken={combinedRefreshToken}
            />
          ))}
        </div>
      )}

      {/* Floating scroll helpers */}
      <div className="scroll-fabs" aria-hidden="false">
        {showJumpUp && (
          <button
            className="scroll-fab up"
            onClick={() => jump("up")}
            aria-label="Jump up"
            title="Jump up"
          >▲</button>
        )}
        {showJumpDown && (
          <button
            className="scroll-fab down"
            onClick={() => jump("down")}
            aria-label="Jump down"
            title="Jump down"
          >▼</button>
        )}
      </div>

      {/* Bottom Save bar */}
      {notes.length > 0 && (
        <>
          <Hint id="savebar" category="tag" refreshToken={combinedRefreshToken}>
            <b>Save</b> updates this kit. Use <b>Save As</b> to make a copy. <i>Continue takes you to Preview for testing.</i>
          </Hint>
          <div className="actions" style={{ justifyContent: "flex-end", gap: 8, flexWrap: "wrap", marginTop: "16px" }}>
            <div className="actions" style={{ gap: 8 }}>
              <button className="button button-secondary" onClick={onSaveOnly}>Save</button>
              <div ref={saveMenuWrapRef} style={{ position: "relative", display: "inline-block" }}>
                <button
                  ref={saveKebabBtnRef}
                  type="button"
                  className="button button-ghost"
                  aria-haspopup="menu"
                  aria-expanded={saveMenuOpen}
                  onClick={() => setSaveMenuOpen(s => !s)}
                  title="More save options"
                >
                  <MoreVertical size={18} aria-hidden />
                </button>
                {saveMenuOpen && (
                  <div id="save-kebab-menu" role="menu" aria-label="Save options"
                    style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", minWidth: 160, background: "#fff",
                      border: "1px solid #e5e7eb", borderRadius: 8, boxShadow: "0 8px 20px rgba(0,0,0,0.12)", padding: 6, zIndex: 20 }}>
                    <button role="menuitem" type="button" className="button button-ghost"
                      onClick={() => { setSaveMenuOpen(false); onSaveAs(); }} style={{ width: "100%", justifyContent: "flex-start" }}>
                      Save As…
                    </button>
                  </div>
                )}
              </div>
              <button className="button button-primary" onClick={onSaveAndPreview}>Save and Continue to Preview</button>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
